/** SUNTRONIX ONE — Apple Compatibility Studio (visual rules editor drawer). */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ShieldCheck, Plus, Trash2, Pencil, Sparkles } from "lucide-react";

const SEV_STYLES = {
  BLOCK: "bg-rose-100 text-rose-800 border-rose-200",
  WARN: "bg-amber-100 text-amber-800 border-amber-200",
};

const EMPTY_RULE = {
  name: "", notes: "", scope_family: "", scope_base_model_id: "",
  when_kind: "CHIP", when_values: [],
  rule_type: "REQUIRE", then_kind: "UNIFIED_MEMORY", then_values: [],
  severity: "BLOCK", active: true,
};

export const AppleCompatStudio = ({ meta, onClose }) => {
  const [rules, setRules] = useState([]);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() =>
    api.get("/apple/compat/rules").then(({ data }) => setRules(data.items || [])), []);
  useEffect(() => { load(); }, [load]);

  const seed = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/apple/compat/seed-rules");
      toast.success(`Apple guardrails loaded — ${data.inserted} new · ${data.updated} refreshed`);
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Seed failed"); }
    finally { setBusy(false); }
  };

  const del = async (r) => {
    if (!window.confirm(`Delete rule "${r.name}"?`)) return;
    try {
      await api.delete(`/apple/compat/rules/${r.id}`);
      toast.success("Rule deleted");
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const toggleActive = async (r) => {
    try {
      await api.patch(`/apple/compat/rules/${r.id}`, { active: !r.active });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || "Update failed"); }
  };

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="apple-compat-studio">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative w-[780px] max-w-full h-full bg-white shadow-xl overflow-y-auto">
        <div className="p-4 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-5 w-5 text-emerald-700" />
            <div>
              <div className="text-[10px] uppercase text-slate-500">Guardrails</div>
              <div className="text-[14px] font-heading font-semibold">Apple Compatibility Studio</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={seed} disabled={busy}
                data-testid="compat-seed-rules"
                className="text-[11px] px-2.5 py-1.5 rounded-md border border-emerald-600 text-emerald-800 hover:bg-emerald-50 inline-flex items-center gap-1 disabled:opacity-40">
              <Sparkles className="h-3 w-3" /> Load Apple guardrails
            </button>
            <button onClick={() => setEditing({ ...EMPTY_RULE })}
                data-testid="compat-new-rule"
                className="text-[11px] px-2.5 py-1.5 rounded-md bg-slate-900 text-white inline-flex items-center gap-1">
              <Plus className="h-3 w-3" /> New rule
            </button>
            <button onClick={onClose} data-testid="compat-close"
                className="text-slate-400 hover:text-slate-700">✕</button>
          </div>
        </div>

        {editing ? (
          <RuleEditor meta={meta} rule={editing}
            onCancel={() => setEditing(null)}
            onSaved={async () => { setEditing(null); await load(); }} />
        ) : (
          <div className="p-4 space-y-2">
            {rules.length === 0 && (
              <div className="text-center py-10 text-[12px] text-slate-500 space-y-2">
                <div>No compatibility rules yet.</div>
                <div>Click <b>Load Apple guardrails</b> to seed real 2026 rules
                  (e.g. M5 Pro Max ⇒ 48GB+ memory), or create your own.</div>
              </div>
            )}
            {rules.map((r) => (
              <div key={r.id}
                  data-testid={`compat-rule-${r.id}`}
                  className={"border rounded-lg p-3 " +
                    (r.active ? "border-slate-200 bg-white" : "border-slate-100 bg-slate-50 opacity-60")}>
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={"text-[9px] font-semibold px-1.5 py-0.5 rounded border " + SEV_STYLES[r.severity]}>
                    {r.severity === "BLOCK" ? "HARD BLOCK" : "WARN"}
                  </span>
                  <span className="text-[9px] px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {r.scope_family || "All families"}
                  </span>
                  <span className="text-[12px] font-semibold">{r.name}</span>
                  <div className="flex-1" />
                  <label className="text-[10px] text-slate-500 flex items-center gap-1 cursor-pointer">
                    <input type="checkbox" checked={!!r.active} onChange={() => toggleActive(r)}
                        data-testid={`compat-rule-active-${r.id}`} />
                    active
                  </label>
                  <button onClick={() => setEditing({ ...r })}
                      data-testid={`compat-rule-edit-${r.id}`}
                      className="text-[10.5px] px-2 py-1 rounded border border-slate-300 hover:bg-slate-50 inline-flex items-center gap-1">
                    <Pencil className="h-3 w-3" /> Edit
                  </button>
                  <button onClick={() => del(r)}
                      data-testid={`compat-rule-delete-${r.id}`}
                      className="text-[10.5px] px-2 py-1 rounded border border-rose-400 text-rose-700 hover:bg-rose-50 inline-flex items-center gap-1">
                    <Trash2 className="h-3 w-3" />
                  </button>
                </div>
                <div className="mt-2 flex items-center gap-2 flex-wrap text-[11px]">
                  <span className="text-slate-500">WHEN</span>
                  <span className="font-semibold">{r.when_kind?.replace(/_/g, " ")}</span>
                  <span className="text-slate-400">∈</span>
                  {(r.when_values || []).map((v) => (
                    <span key={v} className="px-1.5 py-0.5 rounded bg-slate-900 text-white text-[10px] font-mono">{v}</span>
                  ))}
                  <span className={"mx-1 font-semibold " + (r.rule_type === "FORBID" ? "text-rose-700" : "text-emerald-700")}>
                    → {r.rule_type === "FORBID" ? "FORBID" : "REQUIRE"}
                  </span>
                  <span className="font-semibold">{r.then_kind?.replace(/_/g, " ")}</span>
                  <span className="text-slate-400">∈</span>
                  {(r.then_values || []).map((v) => (
                    <span key={v} className={"px-1.5 py-0.5 rounded text-[10px] font-mono " +
                        (r.rule_type === "FORBID" ? "bg-rose-100 text-rose-800" : "bg-emerald-100 text-emerald-800")}>{v}</span>
                  ))}
                </div>
                {r.notes && <div className="text-[10px] text-slate-400 mt-1">{r.notes}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};


function RuleEditor({ meta, rule, onCancel, onSaved }) {
  const [r, setR] = useState(rule);
  const [vocab, setVocab] = useState({});
  const [bases, setBases] = useState([]);
  const [busy, setBusy] = useState(false);
  const kinds = meta?.option_kinds || [];
  const families = (meta?.families || []).map((f) => f.name);

  useEffect(() => {
    api.get("/apple/compat/vocab", { params: r.scope_family ? { family: r.scope_family } : {} })
      .then(({ data }) => { setVocab(data.vocab || {}); setBases(data.bases || []); })
      .catch(() => {});
  }, [r.scope_family]);

  const toggleVal = (field, v) => {
    setR((x) => {
      const cur = x[field] || [];
      return { ...x, [field]: cur.includes(v) ? cur.filter((y) => y !== v) : [...cur, v] };
    });
  };

  const save = async () => {
    if (!r.name) { toast.error("Rule name required"); return; }
    if (!(r.when_values || []).length || !(r.then_values || []).length) {
      toast.error("Pick at least one WHEN value and one THEN value"); return;
    }
    setBusy(true);
    const body = {
      name: r.name, notes: r.notes || null,
      scope_family: r.scope_family || null,
      scope_base_model_id: r.scope_base_model_id || null,
      when_kind: r.when_kind, when_values: r.when_values,
      rule_type: r.rule_type, then_kind: r.then_kind, then_values: r.then_values,
      severity: r.severity, active: r.active !== false,
    };
    try {
      if (r.id) await api.patch(`/apple/compat/rules/${r.id}`, body);
      else await api.post("/apple/compat/rules", body);
      toast.success(r.id ? "Rule updated" : "Rule created");
      await onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const ValuePicker = ({ kind, field }) => {
    const opts = vocab[kind] || [];
    const selected = r[field] || [];
    const custom = selected.filter((v) => !opts.includes(v));
    return (
      <div className="flex flex-wrap gap-1 mt-1">
        {opts.map((v) => (
          <button key={v} type="button" onClick={() => toggleVal(field, v)}
              data-testid={`compat-${field}-chip-${v}`}
              className={"text-[10px] font-mono px-1.5 py-1 rounded border " +
                (selected.includes(v)
                  ? "bg-slate-900 text-white border-slate-900"
                  : "bg-white border-slate-300 text-slate-600 hover:bg-slate-50")}>
            {v}
          </button>
        ))}
        {custom.map((v) => (
          <button key={v} type="button" onClick={() => toggleVal(field, v)}
              className="text-[10px] font-mono px-1.5 py-1 rounded border bg-indigo-900 text-white border-indigo-900">
            {v} ✕
          </button>
        ))}
        <input placeholder="+ custom value ⏎"
            data-testid={`compat-${field}-custom`}
            onKeyDown={(e) => {
              if (e.key === "Enter" && e.target.value.trim()) {
                toggleVal(field, e.target.value.trim());
                e.target.value = "";
              }
            }}
            className="text-[10px] border border-dashed border-slate-300 rounded px-1.5 py-1 w-32" />
      </div>
    );
  };

  return (
    <div className="p-4 space-y-4" data-testid="compat-rule-editor">
      <div className="text-[12px] font-heading font-semibold">
        {r.id ? `Editing: ${rule.name}` : "New compatibility rule"}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <label className="text-[10px] text-slate-500 block col-span-2">
          Rule name *
          <input value={r.name} onChange={(e) => setR({ ...r, name: e.target.value })}
              data-testid="compat-edit-name"
              placeholder='e.g. "M5 Pro Max needs 48GB+ memory"'
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
        </label>
        <label className="text-[10px] text-slate-500 block">
          Applies to family
          <select value={r.scope_family || ""}
              data-testid="compat-edit-family"
              onChange={(e) => setR({ ...r, scope_family: e.target.value, scope_base_model_id: "" })}
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">All families</option>
            {families.map((f) => <option key={f} value={f}>{f}</option>)}
          </select>
        </label>
        <label className="text-[10px] text-slate-500 block">
          Applies to specific model (optional)
          <select value={r.scope_base_model_id || ""}
              data-testid="compat-edit-base"
              onChange={(e) => setR({ ...r, scope_base_model_id: e.target.value })}
              className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5">
            <option value="">Any model in scope</option>
            {bases.map((b) => <option key={b.id} value={b.id}>{b.model_name}</option>)}
          </select>
        </label>
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="text-[10px] uppercase font-semibold text-slate-500">WHEN the buyer selects…</div>
        <select value={r.when_kind}
            data-testid="compat-edit-when-kind"
            onChange={(e) => setR({ ...r, when_kind: e.target.value, when_values: [] })}
            className="mt-1 text-[12px] border border-slate-200 rounded-md px-2 py-1.5 bg-white">
          {kinds.map((k) => <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <ValuePicker kind={r.when_kind} field="when_values" />
      </div>

      <div className="flex items-center gap-3">
        <div className="text-[10px] uppercase font-semibold text-slate-500">Then</div>
        {["REQUIRE", "FORBID"].map((t) => (
          <button key={t} type="button" onClick={() => setR({ ...r, rule_type: t })}
              data-testid={`compat-edit-type-${t}`}
              className={"text-[11px] px-3 py-1.5 rounded-full border font-semibold " +
                (r.rule_type === t
                  ? (t === "FORBID" ? "bg-rose-700 text-white border-rose-700" : "bg-emerald-700 text-white border-emerald-700")
                  : "bg-white border-slate-300 text-slate-600")}>
            {t === "REQUIRE" ? "REQUIRE (must be one of)" : "FORBID (must not be)"}
          </button>
        ))}
      </div>

      <div className="rounded-lg border border-slate-200 bg-slate-50 p-3">
        <div className="text-[10px] uppercase font-semibold text-slate-500">
          …the {r.rule_type === "FORBID" ? "forbidden" : "allowed"} values of
        </div>
        <select value={r.then_kind}
            data-testid="compat-edit-then-kind"
            onChange={(e) => setR({ ...r, then_kind: e.target.value, then_values: [] })}
            className="mt-1 text-[12px] border border-slate-200 rounded-md px-2 py-1.5 bg-white">
          {kinds.filter((k) => k !== r.when_kind).map((k) =>
            <option key={k} value={k}>{k.replace(/_/g, " ")}</option>)}
        </select>
        <ValuePicker kind={r.then_kind} field="then_values" />
      </div>

      <div className="flex items-center gap-3">
        <div className="text-[10px] uppercase font-semibold text-slate-500">Severity</div>
        <button type="button" onClick={() => setR({ ...r, severity: "BLOCK" })}
            data-testid="compat-edit-sev-block"
            className={"text-[11px] px-3 py-1.5 rounded-full border font-semibold " +
              (r.severity === "BLOCK" ? "bg-rose-700 text-white border-rose-700" : "bg-white border-slate-300 text-slate-600")}>
          HARD BLOCK — grey out live
        </button>
        <button type="button" onClick={() => setR({ ...r, severity: "WARN" })}
            data-testid="compat-edit-sev-warn"
            className={"text-[11px] px-3 py-1.5 rounded-full border font-semibold " +
              (r.severity === "WARN" ? "bg-amber-500 text-white border-amber-500" : "bg-white border-slate-300 text-slate-600")}>
          WARN — banner, blocked at Add-to-Quote
        </button>
      </div>

      <label className="text-[10px] text-slate-500 block">
        Notes (optional)
        <input value={r.notes || ""} onChange={(e) => setR({ ...r, notes: e.target.value })}
            data-testid="compat-edit-notes"
            className="mt-1 w-full text-[12px] border border-slate-200 rounded-md px-2 py-1.5" />
      </label>

      <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
        <button onClick={onCancel} disabled={busy}
            className="text-[11px] px-3 py-1.5 rounded-md border border-slate-200">Cancel</button>
        <button onClick={save} disabled={busy}
            data-testid="compat-edit-save"
            className="text-[11px] px-3 py-1.5 rounded-md bg-slate-900 text-white disabled:opacity-40">
          {busy ? "Saving…" : r.id ? "Save rule" : "Create rule"}
        </button>
      </div>
    </div>
  );
}

export default AppleCompatStudio;
